import {
  getGeodesicDistance,
  getSeparationAltitude,
  getCartographicEntityPosition
} from '../../helpers/cesium_helper'

// config: {
//   prefix:  string - ID prefix for DOM lookups unique per table, e.g. 'adsb' or 'efb'
//   columns: [{ key, label, precision? }]
//            - key: property name; resolved from entity first, then entityValues
//            - label: column header text
//            - precision: optional decimal places for toFixed(); omit for string passthrough
// }
export const useDataSourceTable = (_controller, { cesiumViewer, dataSourceId, container, buttonGroup }, config) => {
  const { prefix } = config

  const columns = config.columns.map(col => ({
    ...col,
    render: col.precision != null
      ? (v) => v != null ? v.toFixed(col.precision) : '—'
      : (v) => v != null ? String(v) : '—'
  }))

  let tableItems = []
  let tableContainer
  let table
  let sepTargetEntity
  const tableFilter = {}

  const toggleButton = $('<div class="cesium-button aircraft-table-control-button"><i class="uil uil-list-ul"></i></div>')
  buttonGroup.append(toggleButton)

  toggleButton.on('click', () => {
    const wasHidden = tableContainer.is(':hidden')
    $('.aircraft-table-container').hide()
    if (wasHidden) tableContainer.show()
  })

  const initTable = () => {
    let cssDisplay = 'none'
    if (tableContainer) {
      cssDisplay = tableContainer.css('display')
      tableContainer.remove()
    }

    const filterHtml = `
      <div class="bg-dark aircraft-filter-container p-2">
        <p class='mb-2 text-center text-white'>Show aircraft within</p>
        <div class="aircraft-search-container container-fluid">
          <div class="row">
            <div class="col-sm-6">
              <div class="input-group input-group-sm mb-3">
                <input type="text" id="${prefix}-altitude-sep-filter-input" class="form-control" placeholder="Altitude Sep. (m)" aria-label="Altitude Sep">
                <div class="input-group-append">
                  <button id="${prefix}-clear-altitude-btn" class="btn btn-outline-light" type="button">X</button>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="input-group input-group-sm mb-3">
                <input type="text" id="${prefix}-distance-sep-filter-input" class="form-control" placeholder="Distance Sep. (m)" aria-label="Distance Sep">
                <div class="input-group-append">
                  <button id="${prefix}-clear-distance-btn" class="btn btn-outline-light" type="button">X</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>`

    const sepHeaders = `
      <th scope="col" class='text-center'>Alt.Sep (m)</th>
      <th scope="col" class='text-center'>Dist.Sep (m)</th>`

    const columnHeaders = columns.map(col =>
      `<th scope="col" class='text-center'>${col.label}</th>`
    ).join('')

    const dataTableHtml =
      `<div class='aircraft-table-container' style='display: ${cssDisplay}'>
        ${sepTargetEntity ? filterHtml : ''}
        <table class='table table-hover table-sm table-dark'>
          <thead>
            <tr class='aircraft-row-label-caption'>
              <th scope="col" class='text-center'></th>
              ${columnHeaders}
              ${sepTargetEntity ? sepHeaders : ''}
            </tr>
          </thead>
          <tbody>
          </tbody>
        </table>
      </div>`

    tableContainer = $(dataTableHtml)
    container.append(tableContainer)
    table = tableContainer.find('table')

    const bindFilterInput = (inputKey, filterKey) => {
      const filterInput = tableContainer.find(`#${prefix}-${inputKey}-sep-filter-input`)
      const clearButton = tableContainer.find(`#${prefix}-clear-${inputKey}-btn`)

      const clear = () => {
        filterInput.val(null)
        clearFilterResult(filterKey)
        updateTable()
      }

      filterInput.on('input', (e) => {
        if (e.target.value.length > 0) {
          tableFilter[filterKey] = e.target.value
        } else {
          clear()
        }
      })

      filterInput.on('keydown', (e) => { if (e.key === 'Enter') updateTable() })
      clearButton.on('click', clear)
    }

    bindFilterInput('altitude', 'sepAltitude')
    bindFilterInput('distance', 'sepDistance')
  }

  const clearFilterResult = (col) => {
    delete tableFilter[col]

    if (Object.keys(tableFilter).length === 0) {
      const source = cesiumViewer.dataSources.getByName(dataSourceId)
      if (source.length === 0) return
      source[0].entities.values.forEach((entity) => { entity.show = true })
    }
  }

  const updateTable = () => {
    const dataSource = cesiumViewer.dataSources.getByName(dataSourceId)
    if (!dataSource || dataSource.length === 0) return

    const tableBody = table.find('tbody')
    const entities = dataSource[0].entities.values
    const newTableItems = []

    entities.forEach((entity) => {
      if (entity.isAvailable(cesiumViewer.clock.currentTime)) {
        newTableItems.push(createTableItem(entity))
      }
    })

    if (sepTargetEntity) { newTableItems.sort(sortByAttribute('sepDistance')) }

    if (arraysEqual(newTableItems, tableItems)) {
      tableItems = newTableItems
      tableItems.forEach((item) => updateTableValues(item))
    } else {
      tableItems = newTableItems
      tableBody.children().remove()
      tableItems.forEach((item) => {
        tableBody.append(createTableRow(item))
      })
    }
  }

  const createTableItem = (entity) => {
    const entityValues = entity.properties.getValue('', cesiumViewer.clock.currentTime)

    const item = { id: entity.id, entity, filteredOut: null, sepAltitude: null, sepDistance: null }

    columns.forEach(col => {
      item[col.key] = col.key in entity ? entity[col.key] : entityValues[col.key]
    })

    if (sepTargetEntity && isEntityAvailable(sepTargetEntity)) {
      const entityPoint = getCartographicEntityPosition(cesiumViewer, entity)
      const targetPoint = getCartographicEntityPosition(cesiumViewer, sepTargetEntity)

      if (entityPoint && targetPoint) {
        item.sepDistance = getGeodesicDistance(entityPoint, targetPoint)
        item.sepAltitude = getSeparationAltitude(entityPoint, targetPoint)

        if (tableFilter.sepAltitude < item.sepAltitude ||
            tableFilter.sepDistance < item.sepDistance) {
          item.filteredOut = true
        }
      }
    }

    return item
  }

  // User can change the clock time by dragging the timeline, so we need to check
  // if the entity is available at the current time and clean up stale rows.
  const isEntityAvailable = (entity) => {
    const available = entity.isAvailable(cesiumViewer.clock.currentTime)
    const index = tableItems.findIndex(e => e.id === entity.id)
    const shownInTable = index > -1

    if (!available && shownInTable) {
      table.find('tbody').children().remove(`#${prefix}-row-${entity.id}`)
      tableItems.splice(index, 1)
    }

    return available && !shownInTable
  }

  const updateTableValues = (item) => {
    columns.forEach(col => {
      updateCellValue(item.id, col.key, col.render(item[col.key]))
    })

    if (sepTargetEntity) {
      updateCellValue(item.id, 'sepDistance', item.sepDistance?.toFixed())
      updateCellValue(item.id, 'sepAltitude', item.sepAltitude?.toFixed())

      const checkBoxItem = table.find(`#${prefix}-check-${item.id}`)

      const entity = item.entity
      entity.show = Object.keys(tableFilter).length === 0 ? entity.show : !item.filteredOut

      checkBoxItem.prop('checked', entity.show)
    }
  }

  const updateCellValue = (entityId, attr, value) => {
    const cellItem = table.find(`#${entityId}_${attr}`)
    if (cellItem.length > 0) {
      cellItem[0].innerHTML = value
    }
  }

  const createTableRow = (item) => {
    const entity = item.entity
    const selectedRowClassName = cesiumViewer.selectedEntity?.id === entity.id ? 'aircraft-selected-row' : ''
    if (Object.keys(tableFilter).length > 0) {
      entity.show = !item.filteredOut
    }

    const columnCells = columns.map(col =>
      `<td id='${entity.id}_${col.key}' class='text-right pr-3'>${col.render(item[col.key])}</td>`
    ).join('')

    const sepCells = sepTargetEntity
      ? `<td id='${entity.id}_sepAltitude' class='text-right pr-3'>${item.sepAltitude?.toFixed()}</td>
         <td id='${entity.id}_sepDistance' class='text-right pr-3'>${item.sepDistance?.toFixed()}</td>`
      : ''

    const rowItemHtml =
      `<tr id='${prefix}-row-${entity.id}' class='aircraft-row-label-text ${selectedRowClassName}'>
        <td>
          <div class='form-check ml-2'>
            <input type='checkbox' id='${prefix}-check-${entity.id}' class='form-check-input' ${entity.show ? 'checked' : ''} />
          </div>
        </td>
        ${columnCells}
        ${sepCells}
      </tr>`

    const rowItem = $(rowItemHtml)
    rowItem.on('click', (e) => tableRowOnClick(e, entity))
    return rowItem
  }

  const tableRowOnClick = (e, entity) => {
    if (e.target.type === 'checkbox') {
      entity.show = e.target.checked
    } else {
      e.preventDefault()
      cesiumViewer.selectedEntity = entity
    }
  }

  const sortByAttribute = (attr) => (a, b) => {
    if (a[attr] > b[attr]) return 1
    if (a[attr] < b[attr]) return -1
    return 0
  }

  function arraysEqual (a, b) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (a[i].id !== b[i].id) return false
    }
    return true
  }

  const cesiumEventHelper = new Cesium.EventHelper()

  const cesiumDataSourceListener = () => {
    const ds = cesiumViewer.dataSources.getByName(dataSourceId)

    if (ds.length === 1) {
      cesiumEventHelper.removeAll()
      ds[0].changedEvent.addEventListener(() => updateTable())
    }
  }
  cesiumEventHelper.add(cesiumViewer.dataSources.dataSourceAdded, cesiumDataSourceListener, cesiumEventHelper)

  cesiumViewer.selectedEntityChanged.addEventListener((selectedEntity) => {
    highlightSelectedRow(selectedEntity)
  })

  const timelineBar = document.getElementsByClassName('cesium-viewer-timelineContainer')

  if (timelineBar && timelineBar.length > 0) {
    timelineBar[0].addEventListener('mouseup', () => {
      cesiumViewer.clock.shouldAnimate = true
      updateTable()
    })
  }

  const highlightSelectedRow = (selectedEntity) => {
    table.find('tbody>.aircraft-selected-row').removeClass('aircraft-selected-row')

    if (selectedEntity) {
      table.find(`#${prefix}-row-${selectedEntity.id}`).addClass('aircraft-selected-row')
    }
  }

  initTable()

  return {
    setSeparationTarget (entity) {
      sepTargetEntity = entity
      initTable()
    }
  }
}
